// What the agent thinks on the way to its answer, folded into one item (S137). ocs-capture sends
// a turn's streamed thinking and partial text as `progress` events (deltas, D82), the CLI's
// complete thinking blocks as `assistant` events whose plaintext is the block's JSON
// (`{"type":"thinking","thinking":…}`), and lines it did not recognise as `assistant` events
// marked `unparsed` in their metadata. Shown as they come, that is a wall of raw JSON; here
// each becomes a readable thought, and a run's or a turn's thoughts become one group.
import { toolSummary } from "./display";
import { eventTime, eventTool } from "./hostRows";
import type { EventView } from "./model";
import { readableObject } from "./readable";

export type ThoughtKind = "thinking" | "text" | "tool" | "system";

export interface Thought {
  kind: ThoughtKind;
  text: string;
  /** A tool's name, or a system line's subtype. */
  label?: string;
  at?: number;
  /** A streamed delta the complete message has not replaced yet. */
  partial?: boolean;
  /** A thinking block the model sent redacted: there is nothing to read. */
  redacted?: boolean;
}

export interface ThinkingGroup {
  key: string;
  thoughts: Thought[];
  /** How many events were folded. */
  updates: number;
  startedAt?: number;
  endedAt?: number;
  /** Still streaming: the run or turn has no answer yet. */
  live: boolean;
}

/** `interim`: also fold the agent's interim messages and tool calls (a chat turn's working output). */
export interface FoldOptions {
  interim?: boolean;
}

function object(text: string): Record<string, unknown> | undefined {
  try {
    const v: unknown = JSON.parse(text);
    return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

const meta = (e: EventView, key: string): unknown => (e.meta && typeof e.meta === "object" ? (e.meta as Record<string, unknown>)[key] : undefined);

/** A line ocs-capture could not place: its subtype and a readable body, else the text as sent. */
function systemThought(text: string, at: number | undefined): Thought {
  const o = object(text);
  if (!o) return { kind: "system", text, at };
  const label = [o.type, o.subtype].filter((v): v is string => typeof v === "string").join(" · ") || undefined;
  return { kind: "system", label, text: readableObject(o), at };
}

/** The readable thought an event carries, or undefined when it is not part of the thinking. */
export function thoughtOf(e: EventView, { interim = false }: FoldOptions = {}): Thought | undefined {
  if (e.direction !== "outbound" || e.content.kind !== "plaintext") return undefined;
  const text = e.content.text;
  const at = eventTime(e);
  switch (e.type) {
    case "progress":
      return { kind: meta(e, "kind") === "thinking" ? "thinking" : "text", text, at, partial: true };
    case "assistant": {
      if (meta(e, "unparsed") !== undefined) return systemThought(text, at);
      const block = object(text);
      if (block?.type === "thinking") return { kind: "thinking", text: typeof block.thinking === "string" ? block.thinking : "", at };
      if (block?.type === "redacted_thinking") return { kind: "thinking", text: "", at, redacted: true };
      // A text block of a user-side line (a loaded skill, a reminder the CLI added): its words.
      if (block?.type === "text" && typeof block.text === "string") return { kind: "text", text: block.text, at };
      if (block && typeof block.type === "string") return systemThought(text, at);
      return interim ? { kind: "text", text, at } : undefined;
    }
    case "tool_use":
    case "tool_result":
      return interim ? { kind: "tool", label: eventTool(e) ?? e.type, text: toolSummary(text), at } : undefined;
    default:
      return undefined;
  }
}

/** Whether the event, complete, repeats the text partials streamed before it. */
const completesText = (e: EventView): boolean => e.direction === "outbound" && (e.type === "result" || (e.type === "assistant" && e.content.kind === "plaintext" && object(e.content.text) === undefined));

/**
 * The group of `events` (one run's segment or one turn), in order: consecutive deltas of a kind
 * merge into one thought, and the complete message drops the deltas it repeats. Undefined when
 * nothing in `events` is thinking.
 */
export function groupThoughts(events: EventView[], key: string, options: FoldOptions & { live?: boolean } = {}): ThinkingGroup | undefined {
  const thoughts: Thought[] = [];
  const dropPartials = (kind: ThoughtKind) => {
    for (let i = thoughts.length - 1; i >= 0; i--) if (thoughts[i]?.partial && thoughts[i]?.kind === kind) thoughts.splice(i, 1);
  };
  let updates = 0;
  const times: number[] = [];
  for (const e of events) {
    const t = thoughtOf(e, options);
    if (!t) {
      if (completesText(e)) dropPartials("text");
      continue;
    }
    updates++;
    if (t.at !== undefined) times.push(t.at);
    const last = thoughts.at(-1);
    if (t.partial && last?.partial && last.kind === t.kind) {
      last.text += t.text;
      continue;
    }
    if (!t.partial && (t.kind === "thinking" || t.kind === "text")) dropPartials(t.kind);
    thoughts.push({ ...t });
  }
  if (updates === 0) return undefined;
  return { key, thoughts, updates, startedAt: times.length ? Math.min(...times) : undefined, endedAt: times.length ? Math.max(...times) : undefined, live: options.live ?? false };
}

export type FoldedItem = { kind: "event"; event: EventView } | { kind: "thinking"; group: ThinkingGroup };

/** A finished run's last own message, kept in view when the working is folded and no answer came. */
function lastMessage(events: EventView[]): EventView | undefined {
  return events.findLast((e) => e.direction === "outbound" && e.type === "assistant" && e.content.kind === "plaintext" && object(e.content.text) === undefined);
}

/**
 * One segment's events with its thinking folded into one item, placed just before the run's
 * answer (its `result`, or the last message kept in view), or last while the run is still going. `interim` (the conversation's
 * "Show working" off, S157) also folds the agent's interim messages and tool calls, so the
 * prompt, the questions and the answer stand alone; a run that ended without an answer keeps its
 * last message in view.
 */
export function foldSegment(events: EventView[], key: string, { open = false, interim = false }: { open?: boolean; interim?: boolean } = {}): FoldedItem[] {
  const hasResult = events.some((e) => e.type === "result" && e.direction === "outbound");
  const keep = interim && !hasResult && !open ? lastMessage(events) : undefined;
  const folds = (e: EventView) => e !== keep && thoughtOf(e, { interim }) !== undefined;
  const rest = events.filter((e) => !folds(e));
  // Every event but the one kept in view: the answer still drops the partials it repeats.
  const group = groupThoughts(keep ? events.filter((e) => e !== keep) : events, key, { interim, live: open && !hasResult });
  const items: FoldedItem[] = rest.map((event) => ({ kind: "event", event }));
  if (!group) return items;
  const at = items.findIndex((i) => i.kind === "event" && (i.event === keep || (i.event.type === "result" && i.event.direction === "outbound")));
  items.splice(at < 0 ? items.length : at, 0, { kind: "thinking", group });
  return items;
}

/** Events of several segments, in order, each segment's thinking folded (`isOpen`: still streaming). */
export function foldEvents(events: EventView[], isOpen: (segmentId: string) => boolean): FoldedItem[] {
  const bySegment = new Map<string, EventView[]>();
  for (const e of events) bySegment.set(e.segmentId, [...(bySegment.get(e.segmentId) ?? []), e]);
  return [...bySegment].flatMap(([id, list]) => foldSegment(list, id, { open: isOpen(id) }));
}

/** Plain text of a group, for Copy. */
export function thoughtsText(group: ThinkingGroup, redacted: string): string {
  return group.thoughts
    .map((t) => {
      const body = t.redacted ? redacted : t.text;
      return t.label ? `[${t.label}] ${body}` : body;
    })
    .join("\n\n");
}
